import { useCallback } from "react"
import { create } from "zustand"
import { AddressNS } from "@hg/hg-web-sdk/dist/constants/type"

import { GAME_EQUIP_DICT, useGameBattleStore } from "../data/battle"
import GAME_BLUEPRINT_DICT from "../data/craft"
import { DIALOG_ARR } from "../data/dialog"
import { useGameLayerStore } from "../scene/store"
import { completeGame, Rogue4UpdateType, updateSaveData } from "../service"
import { EGameScene, IGameDialog, IGameDialogUnit, IGameEquip } from "../type"

export const getCraftResult = (key1, key2) => {
    const blueprints = Object.values(GAME_BLUEPRINT_DICT)
    const blueprint = blueprints.find(
        (b) => b.materials.includes(key1) && b.materials.includes(key2)
    )
    if (!blueprint) {
        throw new Error(
            `[GameStore.craft] find no blueprint for ${key1} + ${key2}`
        )
    }
    return blueprint
}

interface IGameStore {
    currentScene: EGameScene | undefined
    setCurrentScene: (value: EGameScene) => void

    currentDialog: IGameDialog
    currentConversation?: IGameDialogUnit
    choice: Record<string, string>
    itemDict: Record<string, boolean>
    equipArr: { key: string; avai: boolean }[]

    gameComplete: boolean
    itemUnlock: Record<string, boolean>
    blueprintUnlock: Record<string, boolean>
    resultRead: Record<string, boolean>
    resultUnlock: Record<string, boolean>

    shared: boolean
    rolled: boolean
    rollResult: {
        bingo: boolean
        giftKey: string
    }
    address?: any
    result?: {
        key: string
        health: { kalts: number; nymph: number }
    }
    battleMusic: boolean

    setCurrentDialog: (value: IGameDialog) => void
    setCurrentConversation: (value: IGameDialogUnit) => void
    choose: (key: string, choice: string) => void
    gainItem: (value: string) => void
    craft: (key1: string, key2: string) => IGameEquip
    readResult: (key) => void
    consumeEquip: (index: number) => void
    setResult: (value: {
        key: string
        health: { kalts: number; nymph: number }
    }) => void
    storeRestart: () => void
    gameDataInit: (saveData: Rogue4SaveData) => void
    rollDateInit: (rollData: Rogue4RollInfo) => void
}

export const useGameStore = create<IGameStore>((set, get) => ({
    currentScene: undefined,
    setCurrentScene: (value) => set({ currentScene: value }),

    currentDialog: DIALOG_ARR[0],
    choice: {},
    itemDict: {},
    equipArr: [],
    itemUnlock: {},
    blueprintUnlock: {},
    resultRead: {},
    resultUnlock: {},
    gameComplete: true,
    shared: false,
    rolled: false,
    rollResult: {
        bingo: false,
        giftKey: "",
    },
    battleMusic: false,
    setCurrentDialog: (value) => {
        set({ currentDialog: value })
    },
    setCurrentConversation: (value) => {
        set({ currentConversation: value })
    },
    choose: (key, choice) => {
        set((state) => ({ choice: { ...state.choice, [key]: choice } }))
    },
    gainItem: (value: string) => {
        const { itemUnlock } = get()
        if (!itemUnlock[value]) updateSaveData(Rogue4UpdateType.ITEM, value)
        set((state) => ({
            itemDict: { ...state.itemDict, [value]: true },
            itemUnlock: { ...state.itemUnlock, [value]: true },
        }))
    },
    craft: (key1, key2) => {
        const blueprint = getCraftResult(key1, key2)
        const { blueprintUnlock } = get()
        if (!blueprintUnlock[blueprint.key])
            updateSaveData(Rogue4UpdateType.EQUIPMENT, blueprint.key)
        set((state) => ({
            itemDict: { ...state.itemDict, [key1]: false, [key2]: false },
            equipArr: [
                ...state.equipArr,
                { key: blueprint.result, avai: true },
            ],
            blueprintUnlock: {
                ...state.blueprintUnlock,
                [blueprint.key]: true,
            },
        }))
        return GAME_EQUIP_DICT[blueprint.result]
    },
    readResult: (key: string) => {
        set((state) => ({
            resultRead: { ...state.resultRead, [key]: true },
        }))
    },
    consumeEquip: (cIndex: number) => {
        set((state) => ({
            equipArr: state.equipArr.map((e, index) => ({
                key: e.key,
                avai: index === cIndex ? false : e.avai,
            })),
        }))
    },
    setResult: (value) => {
        const { resultUnlock } = get()
        if (!resultUnlock[value.key])
            updateSaveData(Rogue4UpdateType.ENDING, value.key)
        completeGame()
        set((state) => ({
            result: value,
            resultUnlock: { ...state.resultUnlock, [value.key]: true },
            gameComplete: true,
        }))
    },
    storeRestart: () => {
        set((state) => ({
            currentDialog: DIALOG_ARR[0],
            currentConversation: DIALOG_ARR[0].dialog[DIALOG_ARR[0].start],
            choice: {},
            itemDict: {},
            equipArr: [],
            battleMusic: false,
        }))
    },
    gameDataInit: (saveData: Rogue4SaveData) => {
        const { items, equipments, endings } = saveData
        set({
            itemUnlock: { ...items },
            blueprintUnlock: { ...equipments },
            resultUnlock: { ...endings },
            resultRead: { ...endings },
            // gameComplete: Object.values(endings).some((b) => b),
        })
    },
    rollDateInit: (rollData: Rogue4RollInfo) => {
        const { game, roll, contact } = rollData
        const address: any | undefined = contact
            ? {
                  id: 0,
                  region: 0,
                  cityData: [0, 0, 0],
                  cityName: [contact?.province, contact?.city, contact?.county],
                  address: contact?.address,
                  areaCode: "",
                  phone: contact?.phone,
                  name: contact.name,
                  isDefault: false,
              }
            : undefined
        set({
            gameComplete: game.complete,
            rolled: roll.used,
            shared: roll.used,
            rollResult: {
                bingo: !!roll.awardId,
                giftKey: roll.awardId ? roll.awardId : "default",
            },
            address,
        })
    },
}))

export const useCurrentScene = () => useGameStore((state) => state.currentScene)

export const useGameProgress = () =>
    useGameStore((state) => ({
        currentDialog: state.currentDialog,
        currentConversation: state.currentConversation,
        choice: state.choice,
        setCurrentDialog: state.setCurrentDialog,
        setCurrentConversation: state.setCurrentConversation,
        choose: state.choose,
        gainItem: state.gainItem,
    }))

export const useGameItemDict = () => useGameStore((state) => state.itemDict)
export const useGameResult = () =>
    useGameStore((state) => ({
        resultUnlock: state.resultUnlock,
        resultRead: state.resultRead,
        readResult: state.readResult,
    }))
export const useGameBlueprintUnlock = () =>
    useGameStore((state) => state.blueprintUnlock)

export const useGameCraft = () =>
    useGameStore((state) => ({
        craft: state.craft,
        equipArr: state.equipArr,
        itemDict: state.itemDict,
    }))

export const useGameEquip = () =>
    useGameStore((state) => ({
        equipArr: state.equipArr,
        consumeEquip: state.consumeEquip,
    }))

export const useGameSetResult = () => useGameStore((state) => state.setResult)

export const useGameRestart = () => {
    const storeRestart = useGameStore((state) => state.storeRestart)
    const battleRestart = useGameBattleStore((state) => state.restart)
    const changeScene = useGameLayerStore((state) => state.changeScene)
    return useCallback(() => {
        storeRestart()
        battleRestart()
        // console.log("restart")
        changeScene(EGameScene.TRAIL)
    }, [battleRestart, changeScene, storeRestart])
}
